import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addCloudDvr'
  | 'bulkAddCloudDvrs'
  | 'getCloudDvr'
  | 'getCloudDvrs'
  | 'updateCloudDvr';

export const useCloudDvrs = () => {
  const [action, setAction] = useState<Action>('');
  const [bulkCloudDvrs, setBulkCloudDvrs] = useState<BulkCloudDvr>();
  const [cloudDvr, setCloudDvr] = useState<CloudDvr | null>(null);
  const [cloudDvrs, setCloudDvrs] = useState<CloudDvr[]>([]);
  const { loading, makeRequest, response } = useFetch();
  const { id: operatorId, cloudDVRs, updateOperatorState } = useOperator();
  const { sortAsc } = useSort<{ mediafirstId: string }>('mediafirstId');
  const BASE_URL = `${BASE_URL_MAP['Residential']}/${operatorId}/CloudDvrs`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'bulkAddCloudDvrs') {
        const bulkResponse = mapBulkResponse(response);
        setBulkCloudDvrs(bulkResponse);
        updateOperatorState('cloudDVRs', [
          ...(cloudDVRs ?? []).concat(bulkResponse.success).sort(sortAsc),
        ]);
      } else if (action === 'updateCloudDvr') {
        const mappedCloudDvr = mapCloudDvr(response);
        updateOperatorState(
          'cloudDVRs',
          [
            mappedCloudDvr,
            ...(cloudDVRs ?? []).filter(
              (cloudDvr) => cloudDvr.id !== mappedCloudDvr.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addCloudDvr') {
        const mappedCloudDvr = mapCloudDvr(response);

        updateOperatorState(
          'cloudDVRs',
          [mappedCloudDvr, ...(cloudDVRs ?? [])].sort(sortAsc)
        );
      } else if (action === 'getCloudDvr') setCloudDvr(mapCloudDvr(response));
      else if (action === 'getCloudDvrs')
        setCloudDvrs(
          response.map((cloudDvr: ApiCloudDvr) => mapCloudDvr(cloudDvr))
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addCloudDvr = (cloudDvr: CreateCloudDvr) => {
    setAction('addCloudDvr');
    const body = mapApiCloudDvr(cloudDvr);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const bulkAddCloudDvr = (cloudDvrs: CreateCloudDvr[]) => {
    setAction('bulkAddCloudDvrs');
    const body = cloudDvrs.map((cloudDvr) => mapApiCloudDvr(cloudDvr));

    makeRequest(`${BASE_URL}/BulkAdd`, 'post', sanitizeValue(body));
  };

  const clearBulkAdds = () => setBulkCloudDvrs({ success: [], fail: [] });

  const getCloudDvr = (cloudDvrId: string) => {
    setAction('getCloudDvr');
    makeRequest(`${BASE_URL}/${cloudDvrId}`, 'get');
  };

  const getCloudDvrs = () => {
    setAction('getCloudDvrs');
    makeRequest(BASE_URL, 'get');
  };

  const updateCloudDvr = (cloudDvr: CloudDvr) => {
    setAction('updateCloudDvr');
    const body: ApiCreateCloudDvr = {
      Active: cloudDvr.active,
      FeatureSubscriptionID: cloudDvr.featureSubscriptionId,
      MediaFirstID: cloudDvr.mediafirstId,
      Description: cloudDvr.description,
    };

    makeRequest(`${BASE_URL}/${cloudDvr.id}`, 'put', sanitizeValue(body));
  };

  const resetState = () => {
    setAction('');
    setBulkCloudDvrs({ success: [], fail: [] });
    setCloudDvr(null);
    setCloudDvrs([]);
  };

  return {
    addCloudDvr,
    cloudDvr,
    cloudDvrs,
    bulkAddCloudDvr,
    bulkCloudDvrs,
    clearBulkAdds,
    getCloudDvr,
    getCloudDvrs,
    loading,
    updateCloudDvr,
  };
};

const mapApiCloudDvr = (cloudDvr: CreateCloudDvr): ApiCreateCloudDvr => ({
  Active: cloudDvr.active,
  MediaFirstID: cloudDvr.mediafirstId,
  FeatureSubscriptionID: cloudDvr.featureSubscriptionId,
  Description: cloudDvr.description,
});

const mapBulkResponse = (
  response: ApiBulkResponse<ApiCloudDvr, ApiCreateCloudDvr>
): BulkCloudDvr => ({
  success: response.Success.map((cloudDvr) => mapCloudDvr(cloudDvr)),
  fail: response.Fail.map((cloudDvr) => ({
    active: cloudDvr.Active,
    mediafirstId: cloudDvr.MediaFirstID,
    featureSubscriptionId: cloudDvr.FeatureSubscriptionID,
    description: cloudDvr.Description,
  })),
});

export const mapCloudDvr = (cloudDvr: ApiCloudDvr): CloudDvr => ({
  id: cloudDvr.ID,
  active: cloudDvr.Active,
  mediafirstId: cloudDvr.MediaFirstID,
  featureSubscriptionId: cloudDvr.FeatureSubscriptionID,
  description: cloudDvr.Description,
});
